<template>
    <div class="flex flex-col gap-4 md:gap-5 fs-mask">
        <div class="grid grid-cols-2 gap-2 md:gap-3 lg:grid-cols-4">
            <FormField id="team-search" :label="t('peopleDirectory.searchTeams')" hide-label class="col-span-2">
                <BaseInput v-model="search" type="search" :aria-label="t('peopleDirectory.searchTeams')" :placeholder="t('peopleDirectory.searchTeamsPlaceholder')">
                    <template #prefix><i class="bi bi-search" aria-hidden="true"></i></template>
                </BaseInput>
            </FormField>
            <FormField id="team-filter" :label="t('peopleDirectory.filter')" hide-label>
                <BaseSelect v-model="filter" :aria-label="t('peopleDirectory.filterTeams')" :options="filterOptions" dropdown-size="tall" />
            </FormField>
            <FormField id="team-sort" :label="t('peopleDirectory.sort')" hide-label>
                <BaseSelect v-model="sort" :aria-label="t('peopleDirectory.sortTeams')" :options="sortOptions" dropdown-size="tall" />
            </FormField>
        </div>
        <p class="details md:body-small text-ink-muted order-last">{{ t("peopleDirectory.teamsPrivacy") }}</p>
        <div v-if="!visibleTeams.length && !loading" class="rounded-lgr border border-border bg-surface p-6">
            <h2 class="title-section text-ink">{{ teams.length ? t("peopleDirectory.noMatchingTeams") : t("peopleDirectory.noTeams") }}</h2>
            <p class="body-small text-ink-muted mt-2">{{ teams.length ? t("peopleDirectory.adjustTeams") : t("peopleDirectory.noTeamsHelp") }}</p>
            <BaseButton v-if="teams.length" theme="secondary" size="sm" class="mt-4" @click="clearFilters">{{ t("peopleDirectory.clearFilters") }}</BaseButton>
        </div>
        <table v-else-if="visibleTeams.length" class="teams-table" :aria-label="t('peopleDirectory.teams')">
            <thead>
                <tr>
                    <th scope="col">{{ t("peopleDirectory.team") }}</th>
                    <th scope="col">{{ t("peopleDirectory.members") }}</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="team in visibleTeams" :key="team.id">
                    <td>
                        <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <BaseLink :href="team.url" class="team-name body-small-bold md:body-bold text-ink hover:underline">{{ team.name }}</BaseLink>
                            <StatusChip v-if="team.is_lead" state="active" size="xs">{{ t("peopleDirectory.youLead") }}</StatusChip>
                        </div>
                        <p class="details md:body-small text-ink-muted mt-1">
                            {{
                                team.leads.length
                                    ? t("peopleDirectory.teamLedBy", { names: team.leads.map((lead) => lead.name).join(", ") })
                                    : t("peopleDirectory.resultCount", { count: team.result_count })
                            }}
                        </p>
                    </td>
                    <td>
                        <div class="flex items-center justify-between gap-2">
                            <p class="details md:body text-ink whitespace-nowrap">{{ t("peopleDirectory.memberCount", { count: team.members.length }) }}</p>
                            <i class="team-chevron bi bi-chevron-right text-ink-muted" aria-hidden="true" />
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>
<script setup lang="ts">
import type { PeopleTeamSummary } from "~vue/api/generated/team-perspective/types.gen";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import FormField from "~vue/components/form/FormField.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
const { teams, loading = false } = defineProps<{ teams: PeopleTeamSummary[]; loading?: boolean }>();
const search = defineModel<string>("search", { default: "" });
const filter = defineModel<string>("filter", { default: "all" });
const sort = defineModel<string>("sort", { default: "name_asc" });
const { t, locale } = useI18n();
const filterOptions = computed(() => [
    { key: "all", label: t("peopleDirectory.all") },
    { key: "lead", label: t("peopleDirectory.teamsYouLead") },
    { key: "member", label: t("peopleDirectory.teamsYouBelongTo") },
]);
const sortOptions = computed(() => [
    { key: "name_asc", label: t("peopleDirectory.nameAsc") },
    { key: "name_desc", label: t("peopleDirectory.nameDesc") },
]);
const visibleTeams = computed(() => {
    const terms = search.value.toLocaleLowerCase(locale.value).trim().split(/\s+/);
    return teams
        .filter((team) => {
            if (filter.value === "lead" && !team.is_lead) {
                return false;
            }
            if (filter.value === "member" && team.is_lead) {
                return false;
            }
            const content = [team.name, ...team.members.map((member) => member.name)].join(" ").toLocaleLowerCase(locale.value);
            return terms.every((term) => content.includes(term));
        })
        .sort((a, b) => a.name.localeCompare(b.name, locale.value) * (sort.value === "name_desc" ? -1 : 1));
});
function clearFilters() {
    search.value = "";
    filter.value = "all";
}
</script>
<style scoped>
@reference "../../../css/tailwind.css";
@layer components {
    .teams-table {
        @apply w-full border-separate border-spacing-0 rounded-lgr border border-border bg-surface-raised;
    }
    .teams-table thead {
        @apply hidden md:table-header-group;
    }
    .teams-table th {
        @apply body-small-semibold text-ink-muted text-left px-5 py-3 border-b border-border;
    }
    .teams-table tbody {
        @apply block md:table-row-group;
    }
    .teams-table tr {
        @apply relative grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-4 py-3 min-h-17 border-b border-border last:border-b-0 md:table-row md:p-0;
    }
    .teams-table td {
        @apply min-w-0 md:table-cell md:px-5 md:py-4 md:border-b md:border-border;
    }
    .team-name::after {
        content: "";
        @apply absolute inset-0;
    }
    .teams-table th:first-child,
    .teams-table td:first-child {
        @apply md:w-3/4;
    }
    .team-chevron {
        @apply md:opacity-0;
    }
    .teams-table tr:hover .team-chevron,
    .teams-table tr:focus-within .team-chevron {
        @apply opacity-100;
    }
    .teams-table tbody tr:last-child td {
        @apply border-b-0;
    }
    .teams-table tbody tr {
        @apply hover:bg-surface-sunken focus-within:bg-surface-sunken;
    }
}
</style>
